import { createResource } from 'react-resource-router';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { assert, AssertionError } from '@atlassian/eoc-common';
import { getCustomTeamRolesEndpoint, getJSMInvitationStatusInfoEndpoint, getMemberRolesPageAccess, getMembers, getRoleAndJSMLicenseInfoEndpoint, getTeam } from '../endpoints';
const mapStatus = (status, userAccessLevel) => {
  if (userAccessLevel === 'INTERNAL_INACTIVE') {
    return 'suspended';
  }
  if (status === 'REQUEST_ACCESS' || status === 'DENIED_REQUEST_EXISTS' || status === 'FORBIDDEN' || status === 'DIRECT_ACCESS') {
    return 'invite';
  }
  if (status === 'ACCESS_EXISTS') {
    return 'member';
  }
  if (status === 'PENDING_REQUEST_EXISTS') {
    return 'pending';
  }
  if (status === '' || status === undefined) {
    return 'member';
  }
  return 'invite';
};
export const memberRolesResource = createResource({
  type: 'memberRolesResource',
  getKey: () => 'memberRolesResource',
  getData: async routerContext => {
    var _routerContext$match$;
    const teamId = (_routerContext$match$ = routerContext.match.params) === null || _routerContext$match$ === void 0 ? void 0 : _routerContext$match$.teamId;
    assert(!!teamId, new AssertionError('memberRolesResource', 'teamId should be set to fetch member-roles'));
    const {
      productOrTeamAdmin: isAuthorizedToSeeMemberRolesPage
    } = await getMemberRolesPageAccess(teamId);
    assert(!!isAuthorizedToSeeMemberRolesPage, new AssertionError('Member Role Unauthorized Page Access', 'You have to have product or team admin permissions to see this page.'));
    const gqlResponse = await getMembers(teamId);
    let members = [];
    if (getBooleanFF('platform.50cent.member-roles-graphql-query-migration_b3hpk')) {
      members = gqlResponse === null || gqlResponse === void 0 ? void 0 : gqlResponse.data.team.teamV2.members.edges.map(edge => edge.node.member);
    } else {
      members = gqlResponse === null || gqlResponse === void 0 ? void 0 : gqlResponse.data.team.team.members.edges.map(edge => edge.node.member);
    }
    const teamMembers = await getRoleAndJSMLicenseInfoEndpoint({
      platformTeamId: teamId,
      accountIds: members.map(member => member.accountId)
    }).then(res => {
      const users = members.map(user => {
        const userWithRole = res.teamMembers.find(userWithRole => userWithRole.accountId === user.accountId);
        return {
          ...user,
          role: (userWithRole === null || userWithRole === void 0 ? void 0 : userWithRole.roleId) || 'user',
          jsmLicense: (userWithRole === null || userWithRole === void 0 ? void 0 : userWithRole.jsmLicense) || false,
          jiraAdmin: (userWithRole === null || userWithRole === void 0 ? void 0 : userWithRole.jiraAdmin) || false,
          invitationStatus: 'member'
        };
      });
      return users;
    });
    const membersWithoutJSMLicense = teamMembers.filter(teamMembers => !teamMembers.jsmLicense);
    if (membersWithoutJSMLicense.length > 0) {
      const accountIds = membersWithoutJSMLicense.map(member => member.accountId).join(',');
      const membersWithFullInfo = await getJSMInvitationStatusInfoEndpoint({
        accountIds
      }).then(res => {
        const users = teamMembers.map(user => {
          const userWithInvitationStatus = res.embeddedValue.find(userWithInvitationStatus => userWithInvitationStatus.accountId === user.accountId);
          return {
            ...user,
            invitationStatus: mapStatus(userWithInvitationStatus === null || userWithInvitationStatus === void 0 ? void 0 : userWithInvitationStatus.accessMode, userWithInvitationStatus === null || userWithInvitationStatus === void 0 ? void 0 : userWithInvitationStatus.userAccessLevel)
          };
        });
        return users;
      }).catch(err => {
        return teamMembers;
      });
      return membersWithFullInfo;
    }
    return teamMembers;
  },
  isBrowserOnly: true
});
export const getTeamResource = createResource({
  type: 'getTeamResource',
  getKey: () => 'getTeamResource',
  getData: async routerContext => {
    var _routerContext$match$2;
    const teamId = (_routerContext$match$2 = routerContext.match.params) === null || _routerContext$match$2 === void 0 ? void 0 : _routerContext$match$2.teamId;
    assert(!!teamId, new AssertionError('getTeamResource', 'teamId should be set to fetch team info on member roles'));
    return getTeam(teamId);
  },
  isBrowserOnly: true
});
export const customTeamRolesResource = createResource({
  type: 'customTeamRolesResource',
  getKey: () => 'customRolesResource',
  getData: async routerContext => {
    var _routerContext$match$3;
    const teamId = (_routerContext$match$3 = routerContext.match.params) === null || _routerContext$match$3 === void 0 ? void 0 : _routerContext$match$3.teamId;
    assert(!!teamId, new AssertionError('customTeamRolesResource', 'teamId should be set to fetch custom-team-roles'));
    return getCustomTeamRolesEndpoint(teamId);
  },
  isBrowserOnly: true
});
export const resources = [memberRolesResource, getTeamResource, customTeamRolesResource];